import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getDonors, deleteDonor, addDonor } from './api';

const AllDonors = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [newDonorForm, setNewDonorForm] = useState({ first_name: '', surname: '', email: '', country_of_residence: '', preferred_activities: '' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDonors();
        // Ensure data is an array
        const donorsArray = Array.isArray(data) ? data : (data?.donors || data?.data || []);
        setDonors(donorsArray);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleViewDonor = (donor) => setSelectedDonor(donor);
  const handleCloseDialog = () => setSelectedDonor(null);

  const handleDeleteDonor = async (donorId) => {
    if (window.confirm('Are you sure you want to delete this donor?')) {
      try {
        await deleteDonor(donorId);
        setDonors(donors.filter(d => d.donor_id !== donorId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleUpdateDonor = (donor) => {
    // Placeholder for update logic (e.g., open a form to edit donor)
    alert(`Update donor ${donor.first_name} ${donor.surname} (ID: ${donor.donor_id})`);
    // Implement update API call and form here
  };

  const handleAddDonor = async () => {
    const activities = newDonorForm.preferred_activities.split(',').map(a => a.trim());
    const newDonor = {
      first_name: newDonorForm.first_name,
      surname: newDonorForm.surname,
      email: newDonorForm.email,
      country_of_residence: newDonorForm.country_of_residence,
      preferred_activities: activities,
      total_contributions: 0,
    };
    try {
      const response = await addDonor(newDonor);
      setDonors([...donors, response]);
      setNewDonorForm({ first_name: '', surname: '', email: '', country_of_residence: '', preferred_activities: '' });
    } catch (error) {
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    setNewDonorForm({ ...newDonorForm, [e.target.name]: e.target.value });
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: '#f5f5f5', minHeight: '100vh', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4,p:4 }}>
        <Typography variant="h4" sx={{ color: '#333', p:4,fontWeight: 'bold', fontSize: isMobile ? '1.5rem' : '2rem' }}>
          
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setSelectedDonor({ mode: 'add' })}
          sx={{
            backgroundColor: theme.palette.success.main,
            color: '#fff',
            '&:hover': { backgroundColor: theme.palette.success.dark },
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 'bold',
            textTransform: 'none',
          }}
        >
          Add Donor
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {!loading && !error && (
        <>
          {/* Stat Cards */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {donors.length}
                  </Typography>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Donors
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Registered worldwide
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(187, 255, 193, 0.12)',
              background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    ${donors.reduce((sum, donor) => sum + (donor.total_contributions || 0), 0).toLocaleString()}
                  </Typography>
                  <AddIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Contributions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  From all donors
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {new Set(donors.map(d => d.country_of_residence)).size}
                  </Typography>
                  <LocationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Countries
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Represented globally
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {!loading && !error && (
        <Grid container spacing={3}>
          {donors.map((donor) => (
            <Grid item xs={12} sm={6} md={4} key={donor.donor_id}>
              <Card
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
                  borderRadius: 3,
                  boxShadow: '0 6px 30px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': { transform: 'scale(1.03)', boxShadow: '0 8px 36px rgba(0,0,0,0.12)' },
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                  <Avatar
                    src={`/avatars/${donor.donor_id}.png`}
                    sx={{ width: 100, height: 100, border: '5px solid #fff', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}
                  />
                  <Typography variant="h6" sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>{donor.first_name} {donor.surname}</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>Country: {donor.country_of_residence}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton onClick={() => handleDeleteDonor(donor.donor_id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleUpdateDonor(donor)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <Button
                      variant="contained"
                      onClick={() => handleViewDonor(donor)}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                        borderRadius: 2,
                        px: 2,
                        py: 0.5,
                        fontWeight: 'bold',
                        textTransform: 'none',
                      }}
                    >
                      View Donor
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Drawer
        anchor="right"
        open={!!selectedDonor}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '40%',
            maxWidth: '100vw',
            height: '100vh',
            background: 'linear-gradient(135deg, #ffffff, #f0f4ff)',
            boxShadow: '-10px 0 30px rgba(0,0,0,0.2)',
          },
        }}
      >
        {selectedDonor && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>
                {selectedDonor.mode === 'add' ? 'Add New Donor' : 'Donor Profile'}
              </Typography>
              <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
            </Box>
            {selectedDonor.mode === 'add' ? (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>Add Donor Details</Typography>
                <input
                  name="first_name"
                  placeholder="First Name"
                  value={newDonorForm.first_name}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <input
                  name="surname"
                  placeholder="Surname"
                  value={newDonorForm.surname}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <input
                  name="email"
                  placeholder="Email"
                  value={newDonorForm.email}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <input
                  name="country_of_residence"
                  placeholder="Country of Residence"
                  value={newDonorForm.country_of_residence}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <input
                  name="preferred_activities"
                  placeholder="Preferred Activities (comma-separated)"
                  value={newDonorForm.preferred_activities}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddDonor}
                  sx={{ backgroundColor: theme.palette.success.main, '&:hover': { backgroundColor: theme.palette.success.dark }, mt: 2 }}
                >
                  Add Donor
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                <Avatar
                  src={`/avatars/${selectedDonor.donor_id}.png`}
                  sx={{ width: 150, height: 150, border: '6px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                />
                <Box sx={{ mt: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#1a2e5c' }}><strong>ID:</strong> {selectedDonor.donor_id}</Typography>
                  <Typography variant="h6" sx={{ color: '#1a2e5c' }}><strong>Name:</strong> {selectedDonor.first_name} {selectedDonor.surname}</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}><strong>Email:</strong> {selectedDonor.email || 'N/A'}</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}><strong>Country:</strong> {selectedDonor.country_of_residence || 'N/A'}</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}><strong>Contributions:</strong> ${selectedDonor.total_contributions || 0}</Typography>
                  <Typography variant="body2" sx={{ color: '#555', mt: 2 }}><strong>Preferred Activities:</strong></Typography>
                  {selectedDonor.preferred_activities && selectedDonor.preferred_activities.length > 0 ? (
                    selectedDonor.preferred_activities.map((activity, index) => (
                      <Box key={index} sx={{ pl: 2, mb: 1, borderLeft: '2px solid #2a9d8f' }}>
                        <Typography variant="body2" sx={{ color: '#1a2e5c' }}>{activity}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: '#777' }}>No preferred activities</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default AllDonors;